import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../store/useStore";
import api from "../../services/api";
import {
  AUTH_LOGO_SRC,
  AuthMessage,
  AuthTabs,
  PasswordVisibilityButton,
  authInputClass,
  authLabelClass,
  authPrimaryButtonClass,
  authSecondaryButtonClass
} from "../auth/AuthUI";
import { COUNTRY_CODE_OPTIONS, buildPhoneWithCountryCode, getFlagUrlForCode } from "../../utils/phoneCountryCodes";

const AuthModal = ({ isOpen, onClose, initialMode = "login" }) => {
  const { login, toast } = useStore();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  const [signupData, setSignupData] = useState({
    companyName: "",
    adminFirstName: "",
    adminLastName: "",
    email: "",
    countryCode: "+220",
    phone: "",
    password: ""
  });

  useEffect(() => {
    setMode(initialMode);
    setLoginError("");
  }, [initialMode]);

  const handleLoginChange = (e) => {
    if (loginError) {
      setLoginError("");
    }
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleSignupChange = (e) => {
    setSignupData({ ...signupData, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!loginData.email || !loginData.password) {
      setLoginError("Please enter email and password");
      toast("Please enter email and password", "error");
      return;
    }

    try {
      setLoginError("");
      setLoading(true);
      const response = await api.post("/auth/login", loginData);

      if (response.data.success) {
        if (typeof login !== 'function') {
          toast("Store error. Please refresh the page.", "error");
          return;
        }
        login(response.data.user, response.data.token);
        toast(`Welcome back, ${response.data.user.firstName}!`, "success");
        setTimeout(() => {
          handleClose();
          navigate("/dashboard");
        }, 500);
      }
    } catch (error) {
      console.error("Login error:", error);
      const message = error.response?.data?.message || "Login failed";
      setLoginError(message);
      toast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    if (!signupData.companyName || !signupData.adminFirstName || !signupData.adminLastName || !signupData.email || !signupData.password) {
      toast("Please fill in all required fields", "error");
      return;
    }

    if (signupData.password.length < 8) {
      toast("Password must be at least 8 characters", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        companyName: signupData.companyName,
        adminFirstName: signupData.adminFirstName,
        adminLastName: signupData.adminLastName,
        email: signupData.email,
        phone: buildPhoneWithCountryCode(signupData.countryCode, signupData.phone),
        password: signupData.password
      };

      const response = await api.post("/auth/signup", payload);

      if (response.data.success) {
        setSuccess(true);
        toast(response.data.message || "Account created successfully!", "success");
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast(error.response?.data?.message || "Failed to create account", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setSuccess(false);
    setLoginError("");
  };

  const handleClose = () => {
    setMode("login");
    setSuccess(false);
    setLoginError("");
    setLoginData({ email: "", password: "" });
    setSignupData({
      companyName: "",
      adminFirstName: "",
      adminLastName: "",
      email: "",
      countryCode: "+220",
      phone: "",
      password: ""
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div
        className="relative bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20 w-full max-w-md max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
        role="dialog"
        aria-modal="true"
        aria-label={mode === "login" ? "Sign in" : "Sign up"}
      >
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 p-2.5 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-500 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="flex flex-col items-center px-6 pt-8 sm:pt-10">
          <img src={AUTH_LOGO_SRC} alt="Gamrent" className="w-16 h-16 rounded-2xl shadow-lg shadow-teal-900/20" />
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">Gamrent</h2>
          <p className="mt-1 text-sm text-slate-500 text-center">
            {mode === "login" ? "Sign in to access your rental dashboard" : "Create your property management account"}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-5">
          <AuthTabs
            active={mode}
            onSignIn={() => mode !== "login" && toggleMode()}
            onSignUp={() => mode !== "signup" && toggleMode()}
            disabled={loading}
          />

          {mode === "login" ? (
            /* LOGIN FORM */
            <form onSubmit={handleLoginSubmit} className="space-y-5">
              {loginError ? (
                <AuthMessage>{loginError}</AuthMessage>
              ) : null}

              <div>
                  <label className={authLabelClass}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    placeholder="you@email.com"
                    className={authInputClass}
                    disabled={loading}
                  />
              </div>

              <div>
                <label className={authLabelClass}>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    placeholder="Enter your password"
                    className={`${authInputClass} pr-12`}
                    disabled={loading}
                  />
                  <PasswordVisibilityButton visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={authPrimaryButtonClass}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          ) : (
            /* SIGNUP FORM */
            <form onSubmit={handleSignupSubmit} className="space-y-5">
              {success ? (
                <div className="rounded-2xl bg-teal-50 p-6 text-center">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-white text-2xl text-teal-700 shadow-sm">✓</div>
                  <p className="text-teal-900 font-bold text-lg">Account Created!</p>
                  <p className="text-sm text-teal-700 mb-2">You can use your email to sign in.</p>
                  <p className="text-teal-700 text-sm mb-4">
                    Contact Gamrent admin to allow access to your dashboard after payment verification.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setMode("login");
                        setLoginData({ email: signupData.email, password: "" });
                        setSuccess(false);
                      }}
                      className={authPrimaryButtonClass}
                    >
                      Go to Sign In
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      className={authSecondaryButtonClass}
                    >
                      Keep for later
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className={authLabelClass}>
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={signupData.companyName}
                      onChange={handleSignupChange}
                      placeholder="e.g. Property Management LLC"
                      className={authInputClass}
                      disabled={loading}
                    />
                  </div>

                  <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
                    <div>
                      <label className={authLabelClass}>
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="adminFirstName"
                        value={signupData.adminFirstName}
                        onChange={handleSignupChange}
                        placeholder="John"
                        className={authInputClass}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className={authLabelClass}>
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="adminLastName"
                        value={signupData.adminLastName}
                        onChange={handleSignupChange}
                        placeholder="Doe"
                        className={authInputClass}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={authLabelClass}>
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={signupData.email}
                      onChange={handleSignupChange}
                      placeholder="your@email.com"
                      className={authInputClass}
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className={authLabelClass}>
                      Phone
                    </label>
                    <div className="grid grid-cols-3 gap-3 items-center">
                      <div className="relative">
                        <img
                          src={getFlagUrlForCode(signupData.countryCode)}
                          alt="flag"
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-6 h-4 object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <select
                          name="countryCode"
                          value={signupData.countryCode}
                          onChange={handleSignupChange}
                          className={`${authInputClass} pl-11 pr-2`}
                          disabled={loading}
                        >
                          {COUNTRY_CODE_OPTIONS.map((item) => (
                            <option key={item.code} value={item.code}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        value={signupData.phone}
                        onChange={handleSignupChange}
                        placeholder="1234567"
                        className={`${authInputClass} col-span-2`}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={authLabelClass}>
                      Password (min 8 chars) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={signupData.password}
                        onChange={handleSignupChange}
                        placeholder="Create a strong password"
                        minLength={8}
                        className={`${authInputClass} pr-12`}
                        disabled={loading}
                      />
                  <PasswordVisibilityButton visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={authPrimaryButtonClass}
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </button>
                </>
              )}
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default AuthModal;
